/**
 * Alshallal Pastry Company HR System - Enhanced Org Chart Page
 * Phase 24.7: Professional hierarchical style with PDF export
 * 
 * Features:
 * - Clean card design with circular photos
 * - Vertical tree layout (top to bottom)
 * - Color-coded nodes (Managers: Blue, HR: Purple, Employees: Green)
 * - Solid connecting lines
 * - PDF download functionality
 * - Pan/zoom navigation
 * - Search/filter
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { hierarchy as d3Hierarchy, tree as d3Tree } from 'd3-hierarchy';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  Search,
  AlertTriangle,
  AlertCircle,
  Users,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Building2,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Mail,
  Phone,
  Download,
  FileText
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------------------
// Layout constants — every dimension is fixed so the tree layout can align
// perfectly. Do not use flex-based sizing inside the tree canvas.
// -----------------------------------------------------------------------------
const NODE_WIDTH = 220;
const NODE_HEIGHT = 132;
const H_SPACING = 32;   // horizontal gap between siblings (added to nodeSize)
const V_SPACING = 60;   // vertical gap between levels

// Professional color palette for node types
const NODE_COLORS = {
  manager: {
    bg: 'bg-blue-500',
    border: 'border-blue-400',
    ring: 'ring-blue-100',
    text: 'text-blue-700',
    stroke: '#60a5fa',
    label: { en: 'Manager', ar: 'مدير' }
  },
  hr: {
    bg: 'bg-purple-500',
    border: 'border-purple-400',
    ring: 'ring-purple-100',
    text: 'text-purple-700',
    stroke: '#a78bfa',
    label: { en: 'HR', ar: 'موارد بشرية' }
  },
  employee: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-400',
    ring: 'ring-emerald-100',
    text: 'text-emerald-700',
    stroke: '#34d399',
    label: { en: 'Employee', ar: 'موظف' }
  }
};

// -----------------------------------------------------------------------------
// Utility: initials from a full name (handles both LTR + RTL scripts).
// -----------------------------------------------------------------------------
const getInitials = (name) => {
  if (!name) return '??';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map(p => p[0]).join('') || '??').toUpperCase();
};

/**
 * OrgChartCard — fixed-size node card. Every card is exactly NODE_WIDTH x
 * NODE_HEIGHT so the d3 tree layout can compute exact positions.
 */
const OrgChartCard = ({ node, isExpanded, onToggle, onNodeClick, searchTerm, isRTL }) => {
  const colors = NODE_COLORS[node.node_type] || NODE_COLORS.employee;
  const hasReports = node.direct_reports_count > 0;

  const searchMatches = searchTerm &&
    (node.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     node.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     node.department?.toLowerCase().includes(searchTerm.toLowerCase()));

  const initials = getInitials(isRTL && node.full_name_ar ? node.full_name_ar : node.full_name);

  return (
    <div
      className={cn(
        "relative bg-white rounded-xl border shadow-sm hover:shadow-md",
        "transition-all duration-150 cursor-pointer overflow-hidden",
        "flex flex-col",
        colors.border,
        searchMatches && "ring-4 ring-yellow-300 border-yellow-400",
        node.flags?.cycle_detected && "ring-4 ring-red-300 border-red-400",
        node.flags?.missing_manager && "ring-2 ring-orange-300 border-orange-400",
      )}
      style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
      onClick={() => onNodeClick(node)}
      data-testid={`org-node-${node.id}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Top color band gives instant role recognition */}
      <div className={cn("h-1", colors.bg)} />

      <div className="flex-1 flex items-center gap-3 px-3 py-2 min-w-0">
        {/* Avatar */}
        <Avatar className={cn("h-11 w-11 shrink-0 ring-2 ring-white border", colors.border)}>
          {node.photo_url ? <AvatarImage src={node.photo_url} alt={node.full_name} /> : null}
          <AvatarFallback className={cn(colors.bg, "text-white font-semibold text-sm")}>
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Text block */}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-800 text-sm truncate leading-tight">
            {isRTL && node.full_name_ar ? node.full_name_ar : node.full_name}
          </div>
          <div className="text-slate-500 text-xs truncate mt-0.5">
            {isRTL && node.job_title_ar ? node.job_title_ar : node.job_title || '—'}
          </div>
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            <Badge className={cn("text-[10px] px-1.5 py-0 h-4 text-white", colors.bg)}>
              {isRTL ? colors.label.ar : colors.label.en}
            </Badge>
            {node.department && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-slate-100 text-slate-600 max-w-[110px] truncate">
                {node.department}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Reports pill (bottom right, doesn't affect layout height) */}
      {hasReports && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
          className={cn(
            "absolute -bottom-2 left-1/2 -translate-x-1/2 z-10",
            "flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full",
            "bg-white border border-slate-200 shadow-sm text-slate-600",
            "hover:bg-slate-50 hover:border-slate-300 transition-colors"
          )}
          data-testid={`org-toggle-${node.id}`}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <Users className="h-3 w-3" />
          <span className="font-medium">{node.direct_reports_count}</span>
        </button>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// OrgTreeCanvas — coordinate-based tree renderer.
//
// Uses d3-hierarchy to compute an exact (x, y) for every visible node,
// then paints:
//   • an SVG layer with elbow connectors between every parent and child, and
//   • an absolutely-positioned div per node card.
//
// This replaces the earlier flex-based `TreeBranch` recursion whose hardcoded
// horizontal-line width was the source of the misaligned connectors.
// -----------------------------------------------------------------------------
const OrgTreeCanvas = ({
  roots,
  nodeMap,
  childrenMap,
  expandedNodes,
  toggleExpand,
  onNodeClick,
  searchTerm,
  isRTL,
}) => {
  // Build a synthetic root that groups every real root together, so a single
  // d3 tree run positions all roots side-by-side with the right spacing.
  const layout = useMemo(() => {
    if (!roots || roots.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };

    // Build hierarchy honoring expanded state — collapsed nodes are leaves.
    const buildChildren = (nodeId, depth) => {
      if (depth > 12) return [];  // depth guard
      if (!expandedNodes.has(nodeId)) return [];
      return (childrenMap[nodeId] || []).map((childId) => ({
        id: childId,
        children: buildChildren(childId, depth + 1),
      }));
    };

    const root = {
      id: '__virtual_root__',
      children: roots.map((rid) => ({ id: rid, children: buildChildren(rid, 1) })),
    };

    const h = d3Hierarchy(root);
    const treeLayout = d3Tree().nodeSize([NODE_WIDTH + H_SPACING, NODE_HEIGHT + V_SPACING]);
    treeLayout(h);

    // d3 tree centers around x=0; translate to positive coordinates and shift
    // the virtual-root row out of view.
    const descendants = h.descendants().filter((d) => d.data.id !== '__virtual_root__');
    if (descendants.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };

    const xs = descendants.map((d) => d.x);
    const ys = descendants.map((d) => d.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Offset every node so the topmost visible row sits at y=0 (with room
    // for the reports pill on top rows), and the leftmost card at x=0.
    const offsetX = -minX + NODE_WIDTH / 2;
    const offsetY = -minY;

    const positioned = descendants.map((d) => ({
      id: d.data.id,
      // d.x is the CENTER; card is drawn from its top-left corner.
      x: d.x + offsetX - NODE_WIDTH / 2,
      y: d.y + offsetY,
      parentId: d.parent && d.parent.data.id !== '__virtual_root__' ? d.parent.data.id : null,
    }));

    const posById = Object.fromEntries(positioned.map((p) => [p.id, p]));

    const edges = positioned
      .filter((p) => p.parentId)
      .map((p) => {
        const parent = posById[p.parentId];
        return {
          key: `${p.parentId}->${p.id}`,
          parentX: parent.x + NODE_WIDTH / 2,
          parentY: parent.y + NODE_HEIGHT,
          childX: p.x + NODE_WIDTH / 2,
          childY: p.y,
        };
      });

    const width = (maxX - minX) + NODE_WIDTH + H_SPACING * 2;
    const height = (maxY - minY) + NODE_HEIGHT + V_SPACING;

    return { nodes: positioned, edges, width, height };
  }, [roots, childrenMap, expandedNodes]);

  if (layout.nodes.length === 0) return null;

  // Elbow connector: down from parent, across horizontally halfway between
  // the two rows, then down into the child. Clean, right-angle, tidy.
  const buildElbow = (e) => {
    const midY = (e.parentY + e.childY) / 2;
    return `M ${e.parentX} ${e.parentY}
            V ${midY}
            H ${e.childX}
            V ${e.childY}`.replace(/\s+/g, ' ');
  };

  return (
    <div
      className="relative"
      style={{
        width: layout.width,
        height: layout.height,
        // Extra breathing room so shadow + reports pill never clip.
        padding: 8,
      }}
    >
      {/* Connector layer */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width={layout.width}
        height={layout.height}
        style={{ overflow: 'visible' }}
      >
        {layout.edges.map((e) => (
          <path
            key={e.key}
            d={buildElbow(e)}
            fill="none"
            stroke="#cbd5e1"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>

      {/* Node layer */}
      {layout.nodes.map((p) => {
        const node = nodeMap[p.id];
        if (!node) return null;
        return (
          <div
            key={p.id}
            className="absolute"
            style={{ left: p.x, top: p.y, width: NODE_WIDTH, height: NODE_HEIGHT }}
          >
            <OrgChartCard
              node={node}
              isExpanded={expandedNodes.has(p.id)}
              onToggle={toggleExpand}
              onNodeClick={onNodeClick}
              searchTerm={searchTerm}
              isRTL={isRTL}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * EmployeeDetailDialog - Popup showing employee details on click
 */
const EmployeeDetailDialog = ({ node, open, onClose, isRTL }) => {
  if (!node) return null;
  
  const colors = NODE_COLORS[node.node_type] || NODE_COLORS.employee;
  
  const initials = node.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '??';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {isRTL ? 'تفاصيل الموظف' : 'Employee Details'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Header with Avatar */}
          <div className="flex flex-col items-center pt-2">
            <div className={cn("p-1.5 rounded-full", colors.ring, "ring-4 mb-3")}>
              <Avatar className="h-20 w-20 border-2 border-white">
                {node.photo_url ? (
                  <AvatarImage src={node.photo_url} alt={node.full_name} />
                ) : null}
                <AvatarFallback className={cn(colors.bg, "text-white text-xl font-bold")}>
                  {initials}
                </AvatarFallback>
              </Avatar>
            </div>
            <h3 className="font-bold text-lg text-slate-800">
              {isRTL && node.full_name_ar ? node.full_name_ar : node.full_name}
            </h3>
            <p className="text-slate-500 text-sm">
              {isRTL && node.job_title_ar ? node.job_title_ar : node.job_title || '-'}
            </p>
            <Badge className={cn("mt-2 text-white", colors.bg)}>
              {isRTL ? colors.label.ar : colors.label.en}
            </Badge>
          </div>

          {/* Details */}
          <div className="space-y-2 pt-2">
            {/* Department */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Building2 className="h-4 w-4 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400">{isRTL ? 'القسم' : 'Department'}</div>
                <div className="text-sm font-medium text-slate-700">{node.department || '-'}</div>
              </div>
            </div>

            {/* Email */}
            {node.email && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Mail className="h-4 w-4 text-slate-400" />
                <div className="flex-1">
                  <div className="text-xs text-slate-400">{isRTL ? 'البريد' : 'Email'}</div>
                  <div className="text-sm font-medium text-slate-700 truncate">{node.email}</div>
                </div>
              </div>
            )}

            {/* Phone */}
            {node.phone && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Phone className="h-4 w-4 text-slate-400" />
                <div className="flex-1">
                  <div className="text-xs text-slate-400">{isRTL ? 'الهاتف' : 'Phone'}</div>
                  <div className="text-sm font-medium text-slate-700">{node.phone}</div>
                </div>
              </div>
            )}

            {/* Direct Reports */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Users className="h-4 w-4 text-slate-400" />
              <div className="flex-1">
                <div className="text-xs text-slate-400">{isRTL ? 'المرؤوسين' : 'Direct Reports'}</div>
                <div className="text-sm font-medium text-slate-700">{node.direct_reports_count || 0}</div>
              </div>
            </div>
          </div>

          {/* View Profile Button */}
          <Button 
            className="w-full" 
            onClick={() => window.location.href = `/employees/${node.id}`}
          >
            {isRTL ? 'عرض الملف الكامل' : 'View Full Profile'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * OrgChartPage - Main org chart visualization page
 */
export const OrgChartPage = () => {
  const { user, token } = useAuth();
  const { isRTL } = useLanguage();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);  // hide flat leaves by default
  
  const transformRef = useRef(null);
  const chartRef = useRef(null);

  // Fetch org chart data
  const fetchOrgChart = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/org-chart/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail?.error || `Failed to fetch: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
      
      // Auto-expand first two levels
      const initialExpanded = new Set(result.roots);
      // Also expand direct children of roots
      result.nodes.forEach(node => {
        if (result.roots.includes(node.manager_id)) {
          initialExpanded.add(node.manager_id);
        }
      });
      setExpandedNodes(initialExpanded);
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrgChart();
  }, [fetchOrgChart]);

  // Build lookup maps
  const { nodeMap, childrenMap } = useMemo(() => {
    if (!data?.nodes) return { nodeMap: {}, childrenMap: {} };
    
    const nMap = {};
    const cMap = {};
    
    data.nodes.forEach(node => {
      nMap[node.id] = node;
      if (node.manager_id) {
        if (!cMap[node.manager_id]) {
          cMap[node.manager_id] = [];
        }
        cMap[node.manager_id].push(node.id);
      }
    });
    
    return { nodeMap: nMap, childrenMap: cMap };
  }, [data]);

  // Partition roots: "real tree" roots (have at least one direct report) vs
  // "orphan" roots (no manager and no reports — flat data with no context).
  // Rendering all 700+ orphan leaves in a single horizontal band destroys the
  // visualisation, so we hide them behind a toggle and surface the count.
  const { treeRoots, orphanRoots } = useMemo(() => {
    if (!data?.roots) return { treeRoots: [], orphanRoots: [] };
    const t = [];
    const o = [];
    data.roots.forEach((rid) => {
      const kids = childrenMap[rid] || [];
      if (kids.length > 0) t.push(rid);
      else o.push(rid);
    });
    return { treeRoots: t, orphanRoots: o };
  }, [data, childrenMap]);

  // Filter roots when searching. In non-search mode, show only the real
  // tree by default; orphans surface only when the user opts in.
  const filteredRoots = useMemo(() => {
    if (!data?.roots) return [];

    if (!searchTerm) {
      return showOrphans ? [...treeRoots, ...orphanRoots] : treeRoots;
    }

    const term = searchTerm.toLowerCase();
    const matchingNodeIds = data.nodes
      .filter(node =>
        node.full_name?.toLowerCase().includes(term) ||
        node.full_name_ar?.toLowerCase().includes(term) ||
        node.job_title?.toLowerCase().includes(term) ||
        node.job_title_ar?.toLowerCase().includes(term) ||
        node.department?.toLowerCase().includes(term)
      )
      .map(n => n.id);

    if (matchingNodeIds.length === 0) return [];

    // Auto-expand ancestors of every hit.
    const newExpanded = new Set(expandedNodes);
    const rootsToShow = new Set();
    matchingNodeIds.forEach(nodeId => {
      let current = nodeMap[nodeId];
      let last = current;
      while (current?.manager_id) {
        newExpanded.add(current.manager_id);
        last = nodeMap[current.manager_id];
        current = last;
      }
      if (last) rootsToShow.add(last.id);
    });
    if (newExpanded.size !== expandedNodes.size) {
      setExpandedNodes(newExpanded);
    }

    return Array.from(rootsToShow);
  }, [data, searchTerm, nodeMap, expandedNodes, treeRoots, orphanRoots, showOrphans]);

  // Toggle node expansion
  const toggleExpand = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Expand all
  const expandAll = useCallback(() => {
    if (!data?.nodes) return;
    setExpandedNodes(new Set(data.nodes.map(n => n.id)));
  }, [data]);

  // Collapse all
  const collapseAll = useCallback(() => {
    if (!data?.roots) return;
    setExpandedNodes(new Set(data.roots));
  }, [data]);

  // Handle node click
  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  // Export to PDF
  const exportToPDF = useCallback(async () => {
    if (!chartRef.current) return;
    
    setExporting(true);
    toast.info(isRTL ? 'جاري إنشاء PDF...' : 'Generating PDF...');
    
    try {
      // Reset zoom before capturing
      transformRef.current?.resetTransform();
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f8fafc',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Create PDF with appropriate dimensions
      const pdf = new jsPDF({
        orientation: imgWidth > imgHeight ? 'landscape' : 'portrait',
        unit: 'px',
        format: [imgWidth * 0.75, imgHeight * 0.75]
      });
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * 0.75, imgHeight * 0.75);
      pdf.save('organization-chart.pdf');
      
      toast.success(isRTL ? 'تم تحميل PDF بنجاح' : 'PDF downloaded successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error(isRTL ? 'فشل في إنشاء PDF' : 'Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  }, [isRTL]);

  // Zoom controls. Reset does a top-aligned fit so the whole tree is visible,
  // rather than dead-centre (which puts flat trees mid-viewport).
  const fitToTop = useCallback(() => {
    const wrapper = transformRef.current;
    if (!wrapper) return;
    // Small pan-in from the left edge; scale is preserved by resetTransform.
    wrapper.setTransform(48, 48, 0.7, 200, 'easeOut');
  }, []);
  const handleZoomIn = () => transformRef.current?.zoomIn();
  const handleZoomOut = () => transformRef.current?.zoomOut();
  const handleReset = () => fitToTop();

  // Auto top-align once the tree has painted. 200ms is enough for the
  // TransformWrapper to accept an initial pan without racing the mount.
  useEffect(() => {
    if (!loading && data && filteredRoots.length > 0) {
      const id = setTimeout(fitToTop, 200);
      return () => clearTimeout(id);
    }
  }, [loading, data, filteredRoots, fitToTop, isRTL]);

  // Access check
  if (!user || !['hr_admin', 'super_admin', 'hr_manager'].includes(user.role)) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <p className="text-slate-600">
              {isRTL 
                ? 'ليس لديك صلاحية لعرض الهيكل التنظيمي' 
                : 'You do not have permission to view the organization chart'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-20 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
            <p className="mt-4 text-slate-500">
              {isRTL ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-10 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={fetchOrgChart}>
              <RefreshCw className="h-4 w-4 mr-2" />
              {isRTL ? 'إعادة المحاولة' : 'Retry'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="org-chart-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'الهيكل التنظيمي' : 'Organization Chart'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isRTL 
              ? `${data?.stats?.total_employees || 0} موظف`
              : `${data?.stats?.total_employees || 0} employees`}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportToPDF}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {isRTL ? 'تحميل PDF' : 'Download PDF'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchOrgChart}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={expandAll}>
            {isRTL ? 'توسيع' : 'Expand All'}
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            {isRTL ? 'طي' : 'Collapse All'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-slate-800">{data?.stats?.total_employees || 0}</div>
            <div className="text-xs text-slate-500">{isRTL ? 'إجمالي' : 'Total'}</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-blue-600">{data?.stats?.managers_count || 0}</div>
            <div className="text-xs text-slate-500">{isRTL ? 'مدراء' : 'Managers'}</div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-purple-600">{data?.stats?.hr_count || 0}</div>
            <div className="text-xs text-slate-500">{isRTL ? 'موارد بشرية' : 'HR Staff'}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-emerald-600">
              {(data?.stats?.total_employees || 0) - (data?.stats?.managers_count || 0) - (data?.stats?.hr_count || 0)}
            </div>
            <div className="text-xs text-slate-500">{isRTL ? 'موظفين' : 'Employees'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Data-quality notice: appears when the tree is mostly-flat (import
          didn't wire manager relationships). Non-blocking, dismissable via
          the toggle above. */}
      {!loading && orphanRoots.length > 0 && (
        <div
          className={cn(
            "flex items-start gap-3 px-4 py-3 rounded-lg border text-sm",
            "bg-amber-50 border-amber-200 text-amber-900"
          )}
          data-testid="org-orphan-notice"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">
              {isRTL
                ? `${orphanRoots.length} موظف بدون مدير مباشر`
                : `${orphanRoots.length} employees have no manager assigned`}
            </div>
            <div className="text-xs mt-0.5 opacity-80">
              {isRTL
                ? 'يظهر الهيكل التنظيمي فقط بعلاقات التقارير الفعلية. استخدم زر "عرض غير المسندين" لإظهارهم كجذور مستقلة.'
                : 'The chart shows only nodes with real reporting lines. Toggle "Show unassigned" to include them as standalone roots.'}
            </div>
          </div>
        </div>
      )}

      {/* Search and Controls */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder={isRTL ? 'بحث...' : 'Search by name, title, or department...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="org-chart-search"
              />
            </div>
            
            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              {orphanRoots.length > 0 && (
                <Button
                  variant={showOrphans ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowOrphans((v) => !v)}
                  data-testid="org-toggle-orphans"
                  title={isRTL
                    ? `${orphanRoots.length} موظف بدون مدير`
                    : `${orphanRoots.length} employees with no manager assigned`}
                >
                  <AlertTriangle className="h-4 w-4 mr-1.5" />
                  {isRTL
                    ? (showOrphans ? 'إخفاء غير المسندين' : `عرض غير المسندين (${orphanRoots.length})`)
                    : (showOrphans ? 'Hide unassigned' : `Show unassigned (${orphanRoots.length})`)}
                </Button>
              )}
              <Button variant="outline" size="icon" onClick={handleZoomOut} data-testid="org-zoom-out">
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleReset} data-testid="org-zoom-reset">
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleZoomIn} data-testid="org-zoom-in">
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm px-1">
        {Object.entries(NODE_COLORS).map(([type, colors]) => (
          <div key={type} className="flex items-center gap-2">
            <div className={cn("w-4 h-4 rounded-full", colors.bg)}></div>
            <span className="text-slate-600">
              {isRTL ? colors.label.ar : colors.label.en}
            </span>
          </div>
        ))}
      </div>

      {/* Org Chart */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {filteredRoots.length === 0 ? (
            <div className="text-center py-10">
              <FileText className="h-10 w-10 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">
                {searchTerm 
                  ? (isRTL ? 'لا توجد نتائج' : 'No results found')
                  : (isRTL ? 'لا يوجد موظفون' : 'No employees')}
              </p>
            </div>
          ) : (
            <TransformWrapper
              ref={transformRef}
              initialScale={0.7}
              initialPositionX={48}
              initialPositionY={48}
              minScale={0.2}
              maxScale={2.5}
              limitToBounds={false}
              wheel={{ step: 0.15 }}
              doubleClick={{ disabled: true }}
              panning={{ velocityDisabled: true }}
            >
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '72vh', background: '#f8fafc', direction: 'ltr' }}
                contentStyle={{ padding: 48, direction: 'ltr' }}
              >
                <div ref={chartRef} style={{ direction: 'ltr' }}>
                  <OrgTreeCanvas
                    roots={filteredRoots}
                    nodeMap={nodeMap}
                    childrenMap={childrenMap}
                    expandedNodes={expandedNodes}
                    toggleExpand={toggleExpand}
                    onNodeClick={handleNodeClick}
                    searchTerm={searchTerm}
                    isRTL={isRTL}
                  />
                </div>
              </TransformComponent>
            </TransformWrapper>
          )}
        </CardContent>
      </Card>

      {/* Employee Detail Dialog */}
      <EmployeeDetailDialog
        node={selectedNode}
        open={!!selectedNode}
        onClose={() => setSelectedNode(null)}
        isRTL={isRTL}
      />
    </div>
  );
};

export default OrgChartPage;
